import React, { useState } from 'react';
import { Save, Clock } from 'lucide-react';
import { AppModal } from '../base/AppModal';
import { AppInput } from '../base/AppInput';
import { AppButton } from '../base/AppButton';
interface InstructionVersionModalProps {
  isOpen: boolean;
  section: 'premessa' | 'logica' | 'output';
  content: string;
  onClose: () => void;
  onSave: (versionName: string) => Promise<void>;
}
export function InstructionVersionModal({
  isOpen,
  section,
  content,
  onClose,
  onSave
}: InstructionVersionModalProps) {
  const [versionName, setVersionName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const sectionLabels = {
    premessa: 'Premessa',
    logica: 'Logica',
    output: 'Output'
  };
  const handleSave = async () => {
    if (!versionName.trim()) return;
    setIsSaving(true);
    try {
      await onSave(versionName.trim());
      setVersionName('');
      onClose();
    } catch (error) {
      console.error('Failed to save version', error);
    } finally {
      setIsSaving(false);
    }
  };
  const generateVersionName = () => {
    const now = new Date();
    const timestamp = now.toLocaleString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
    setVersionName(`${sectionLabels[section]} - ${timestamp}`);
  };
  return <AppModal isOpen={isOpen} onClose={onClose} title={`Salva Nuova Versione - ${sectionLabels[section]}`} data-id="element-315">
      <div className="space-y-6" data-id="element-316">
        {/* Info */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-900/30 rounded-xl" data-id="element-317">
          <Clock className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" data-id="element-318" />
          <div data-id="element-319">
            <p className="text-sm font-medium text-blue-900 dark:text-blue-200 mb-1" data-id="element-320">
              Versioning delle istruzioni
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300" data-id="element-321">
              Salva una snapshot del contenuto corrente della sezione{' '}
              <strong data-id="element-322">{sectionLabels[section]}</strong>. Potrai ripristinarla in
              qualsiasi momento.
            </p>
          </div>
        </div>

        {/* Content Preview */}
        <div data-id="element-323">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-324">
            Contenuto da salvare
          </label>
          <div className="p-3 bg-gray-50 dark:bg-dark-subtle border border-gray-200 dark:border-dark-medium rounded-xl max-h-32 overflow-y-auto text-xs text-gray-600 dark:text-gray-400 font-mono" data-id="element-325">
            {content.substring(0, 200)}
            {content.length > 200 && '...'}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-326">
            {content.length} caratteri
          </p>
        </div>

        {/* Version Name */}
        <div data-id="element-327">
          <AppInput label="Nome Versione" value={versionName} onChange={e => setVersionName(e.target.value)} placeholder="Es. Versione con nuove policy" helperText="Scegli un nome descrittivo per identificare questa versione" data-id="element-328" />
          <button onClick={generateVersionName} className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium" data-id="element-329">
            Genera nome automatico
          </button>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4" data-id="element-330">
          <AppButton variant="secondary" onClick={onClose} fullWidth data-id="element-331">
            Annulla
          </AppButton>
          <AppButton onClick={handleSave} fullWidth disabled={isSaving || !versionName.trim()} icon={<Save className="w-4 h-4" data-id="element-333" />} data-id="element-332">
            {isSaving ? 'Salvataggio...' : 'Salva versione'}
          </AppButton>
        </div>
      </div>
    </AppModal>;
}